import { formatVal, formatDate, formatINR } from "../utils/formatters";

export default function BookingsTabTable({ bookings, emptyMessage, showPayment = false }) {
  if (!bookings || bookings.length === 0) {
    return <div className="p-4 text-sm text-gray-600">{emptyMessage}</div>;
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-t bg-white">
            <tr className="text-left">
              <th className="px-4 py-3">Booking ID</th>
              <th className="px-4 py-3">Origin</th>
              <th className="px-4 py-3">Destination</th>
              <th className="px-4 py-3">Travel Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Amount</th>
            </tr>
          </thead>
          <tbody className="border-t">
            {bookings.map((b) => (
              <tr key={b.booking_id || b.id || Math.random()} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold">{formatVal(b.booking_id ?? b.id)}</td>
                <td className="px-4 py-3">{formatVal(b.origin)}</td>
                <td className="px-4 py-3">{formatVal(b.destination)}</td>
                <td className="px-4 py-3 text-gray-600">
                  {formatDate(b.travel_date ?? b.travel_date_from ?? b.dep_date_time)}
                </td>
                <td className="px-4 py-3">
                  {b.status === 1 || b.status === "1"
                    ? "Active"
                    : b.status === 0 || b.status === "0"
                      ? "Inactive"
                      : b.cancellation_type === 2
                        ? "Cancelled"
                        : b.cancellation_type === 1
                          ? "Partial Cancel"
                          : formatVal(b.status)}
                </td>
                <td className="px-4 py-3 tabular-nums">
                  {formatINR(b.amount ?? b.total_amount ?? b.totalfare)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPayment &&
        bookings.map((b) => {
          if (!b.payment && !b.totalfare) return null;
          const method = b.payment?.method || "—";
          const payStatus = b.payment?.payment_status || "—";
          const paidAtRaw = b.payment?.paid_at;
          const paidAt = (() => {
            if (!paidAtRaw) {
              return b.created_datetime
                ? new Date(b.created_datetime).toLocaleString("en-IN")
                : "—";
            }
            const d = new Date(paidAtRaw);
            if (isNaN(d.getTime())) return "—";
            if (d.getFullYear() < 2000) {
              return b.created_datetime
                ? new Date(b.created_datetime).toLocaleString("en-IN")
                : "—";
            }
            return d.toLocaleString("en-IN");
          })();

          const baseFareNum = Number(b.base_fare) || 0;
          const airlineTaxNum = Number(b.airline_tax) || 0;
          const convFeeNum = Number(b.convenience_fee) || 0;
          const actualTotal = Number(b.totalfare) || 0;
          const expectedTotal = baseFareNum + airlineTaxNum + convFeeNum;
          if (Math.abs(expectedTotal - actualTotal) > 2) {
            console.warn(
              `[fare-breakdown] Booking ${b.booking_id ?? b.id} components don't reconcile. ` +
                `Expected: ${formatINR(expectedTotal)}, Actual: ${formatINR(actualTotal)}, ` +
                `Difference: ${formatINR(expectedTotal - actualTotal)}. ` +
                `Components: base=${baseFareNum}, airline_tax=${airlineTaxNum}, convenience=${convFeeNum}.`
            );
          }

          return (
            <div key={`pay-${b.booking_id ?? b.id}`} className="border-t px-4 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Booking Ref (Navigatio)</div>
                  <div className="font-semibold">{b.booking_ticket_number || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Airline Booking ID</div>
                  <div className="font-semibold">
                    {b.air_booking_id || (
                      <span className="text-orange-500 text-xs">Not stored — check the airline system</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Airline</div>
                  <div className="font-semibold">
                    {b.airline_code} {b.airline_flight_no ? `— ${b.airline_flight_no}` : ""}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Airline PNR</div>
                  <div className="font-semibold">{b.airline_pnr || "—"}</div>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  💰 Fare Breakdown
                </div>
                <div className="rounded-xl bg-gray-50 border p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <span>{formatINR(b.base_fare)}</span>
                  </div>
                  {Number(b.airline_tax) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Airline Taxes & Fees</span>
                      <span>{formatINR(b.airline_tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Convenience Fee</span>
                    <span>{formatINR(b.convenience_fee)}</span>
                  </div>

                  {Number(b.applied_promo_code) === 1 && Number(b.applied_promo_code_amount) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">
                        Promo Discount
                        {b.promo_code && (
                          <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                            {b.promo_code}
                          </span>
                        )}
                      </span>
                      <span className="text-green-700">− {formatINR(b.applied_promo_code_amount)}</span>
                    </div>
                  )}
                  {Number(b.is_wallet_applied) === 1 && Number(b.wallet_points) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700">NavCash Used</span>
                      <span className="text-blue-700">− {formatINR(b.wallet_points)}</span>
                    </div>
                  )}
                  {(b.is_giftcard_applied === true || Number(b.is_giftcard_applied) === 1) &&
                    Number(b.applied_giftcard_amount) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-purple-700">
                        Gift Card Applied
                        {b.is_giftcard_fully_claimed && (
                          <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">
                            Fully Claimed
                          </span>
                        )}
                      </span>
                      <span className="text-purple-700">− {formatINR(b.applied_giftcard_amount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
                    <span>Total Charged</span>
                    <span>{formatINR(b.totalfare)}</span>
                  </div>
                </div>
              </div>

              {b.payment && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    💳 Payment Info
                  </div>
                  <div className="rounded-xl bg-gray-50 border p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Method</div>
                      <div className="font-semibold capitalize">{method}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Status</div>
                      <div
                        className={`font-semibold capitalize ${
                          payStatus === "captured"
                            ? "text-green-700"
                            : payStatus === "authorized"
                              ? "text-blue-700"
                              : payStatus === "failed"
                                ? "text-red-700"
                                : "text-gray-700"
                        }`}
                      >
                        {payStatus}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Amount Paid</div>
                      <div className="font-semibold">
                        ₹{Number(b.payment.amount).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Paid At</div>
                      <div className="font-semibold text-xs">{paidAt}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500">Payment Reference</div>
                      <div className="font-mono text-xs font-semibold break-all">
                        {b.payment.payment_id || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </>
  );
}
